import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, of, Subscription } from 'rxjs';
import { switchMap, map, filter, take, tap, withLatestFrom, mergeMap, ignoreElements } from 'rxjs/operators';
import * as moment from 'moment';
import { getCountry, getLegalEntity, getParameters } from '@cemex/cmx-client-layout';

import * as fromDCOActions from './modules/dco/redux/actions/dco.action';
import * as fromDCOScheduleActions from '@ordersnproducts/modules/multiple-order-online/modules/dco/redux/actions/dco-schedule.action';

import {
  bigOrderQty,
  selectActiveWorksheet,
  selectCalendarResponse,
  selectPurchaseOrderValidation,
  selectWorksheetByShipmentLocation,
} from './redux/selectors/shipment-location-worksheets.selector';
import { AsyncShipmentLocationWorkSheet } from '@ordersnproducts/modules/multiple-order-online/redux/reducers/shipment-location-worksheets.reducer';
// access modal
import ShipmentLocationActions from 'app/shared/upload-document/redux/actions/shipment-location.actions';
import {
  selectActiveProductTypeId,
  selectAllProductTypes,
  selectProductTypeState,
} from 'app/shared/upload-document/redux/selectors/product-type.selector';
import {
  selectActiveShipmentLocationEntity,
  selectAllShipmentLocations,
  selectJobsitesStatus,
} from 'app/shared/upload-document/redux/selectors/shipment-location.selector';
import ProductTypeActions from 'app/shared/upload-document/redux/actions/product-type.actions';

import {
  getOrdersForms,
  getTotalNumberOfOrdersForms,
  getSelectedOrderForm,
  getFormWithId,
  getOrderFormsWithOrderConfirmation,
  getMaximumNumberOfOrders,
} from './redux/selectors/orders-forms.selector';
import OrdersFormsAction, { OrdersFormsActions } from './redux/actions/orders-forms.action';
import OrdersCatalogsActions from './redux/actions/orders-catalogs.action';
import { KeyValues, OrderForm } from './redux/reducers/orders-forms.reducer';
import {
  getApplications,
  getContacts,
  getLoadSizes,
  getProducts,
  getSlumps,
  getSpacings,
  getUnloadTypes,
  getValuesAddedForKey,
  getUnloadTimes,
  getPODs,
  getOrdersCatalogsState,
  getFeatureTemperatureManagement,
  getFeatureIceQuantity,
  getAvailableValuesAdded,
  getPumpPrimerDefaultQuantity,
  getPumpMethodValue,
  getExtraProducts,
  getUnitsByProductMap,
} from '@ordersnproducts/modules/multiple-order-online/redux/selectors/orders-catalogs.selector';
import { getDcoEntitiesAsArray } from '../shared/reducers';
import { MultipleOrderOnlineState, getSpecialScenariosMooState } from './redux/reducers';
import OrderScheduleActions from './redux/actions/schedule-order-confirmation.actions';
import { loadOrderConfirmation } from './redux/selectors/order-confirmation.selector';
import ShipmentLocationWorkSheetActions, {
  ShipmentLocationWorkSheet,
} from './redux/actions/shipment-location-worksheets.actions';
import ScheduleDatesActions from './redux/actions/schedule-dates.action';
import {
  getSelectedSlot,
  getSlotsMOO,
  getSpecialScenariosState,
  getSpecialScenariosWithFormId,
  getVolumeAllocation,
} from './modules/dco/redux/reducers';
import { ResetBuilderAction } from '../order-builder/actions/order-builder.actions';
import {
  getGroupRequest,
  getLoadingOrderConfirmation,
} from '@ordersnproducts/modules/multiple-order-online/redux/selectors/schedule-order-confirmation.selector';
import { getSlot, getErrorSlotCode } from '@ordersnproducts/modules/multiple-order-online/redux/selectors/dco.selector';
import {
  getCalendarState,
  getCutOffAndCalendarResponse,
  getCutOffAndOfferDays,
  getOfferSlots,
  getSelectedSlotAndTravelTime,
} from './redux/selectors/multiple-order-online.selector';
import { SpecFieldNames } from '../order-builder/modules/specification/constants/specification-field-names.enum';
import { _safe } from '../shared/utils/safe-get.method';
import { COUNTRY_CODE, ConcreteProposalOP } from '@cemex-core/cmx-api';
import { APPLICATION_FIELD_NAME } from './modules/controllers/fields/application.field';
import { Country, getAppInjector, makeReference } from '@ordersnproducts/utils/mix-reference';
import { SPECIAL_SCENARIO_KEYS } from './modules/dco/models/special-scenarios-keys.enum';
import { ErrorCode } from '../shared/models/slots-error-code.enum';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { getExperienceByName } from '@ordersnproducts/utils/get-experience.helper';
import { TEMPERATURE_CONTROL } from '@ordersnproducts/modules/shared/constants/temperature-control';
import { ImFeature } from '@cemex-core/cmx-api/models/catalogs/catalogs-response';
import { BusinessDay, Calendar } from '@cemex-core/cmx-api/models/calendars/calendar';
import { TemperatureControlEnum } from '../shared/models/temperature-control.enum';
import { getReceivingPersonEntities } from '../shared/modules/receiving-person/redux/selectors';
import { UntypedFormGroup } from '@angular/forms';
import { isPumpUnloadType } from '@ordersnproducts/utils/pump-utils';
import {
  PP_PUMP_MED_MULTIPLE_VALUE,
  PP_PUMP_MED_VALUE,
  PUMP_NONE_CODE,
  UNLOAD_TYPE_DEFAULT1,
  UNLOAD_TYPE_DEFAULT2,
} from 'app/shared/pumping/pumping.constant';
import { BusinessDayType } from '@ordersnproducts/utils/utils';
import { getUserRoles } from '@ordersnproducts/store';
import { RolesCollection } from '@ordersnproducts/utils';
import { AsyncState } from '@ordersnproducts/utils/create-async-state';
import { selectAllExperiences } from 'app/shared/upload-document/redux/selectors/experiences.selector';
import { ORDER_LIMIT_KEY } from 'app/pages/bulk-cement/shared/constants/orders-limit-key';

const MO_MAXIMUM_ORDER_LIMITS = 'MO_MAXIMUM_ORDER_LIMITS';

@Injectable({
  providedIn: 'root',
})
export class MultipleOrderOnlineService {
  // @TODO
  // Inject Current user service for role
  // Inject Feature toggle service f
  // Remove ANY From store generic

  public country: Country;
  // Default Day Batch size
  public daysBatchSize = 6;
  public visibleSlotCount$ = new BehaviorSubject(this.daysBatchSize);
  private activeSpecialScenarios$ = new BehaviorSubject<string[]>([]);
  private activeSpecialScenariosSubscription: Subscription;

  constructor(
    private store: Store<MultipleOrderOnlineState>,
    private featureToggle: FeatureToggleService,
  ) {
    const injector = getAppInjector();
    this.country = Country.getInstance(injector);
    this.country.getCountryCode();

    // Update Store for get maximum number of Orders form Parameters API
    this.store
      .select(getParameters)
      .pipe(
        take(1),
        map(
          (parameters) =>
            parameters.find((parameterItem) => parameterItem.parameterDesc === MO_MAXIMUM_ORDER_LIMITS)?.parameterValue,
        ),
        filter<string>(Boolean),
        map(Number),
      )
      .subscribe((maximumNumberOfOrders) => {
        this.store.dispatch(OrdersFormsActions.updateLimitOfOrderForm({ maximumNumberOfOrders }));
      });
  }

  /**
   * Sets a shipment location as the active worksheet in MOO view.
   * @param id The jobsite/shipment location id.
   */
  public setActiveShipmentLocationWorksheet(id: number): void {
    this.store.dispatch(ShipmentLocationWorkSheetActions.setActive({ shipmentLocationId: id }));
  }

  /**
   * Retrieves the active shipment location worksheet from the state.
   * @returns The active shipment location worksheet entity.
   */
  public getActiveShipmentLocationWorksheet$(): Observable<AsyncShipmentLocationWorkSheet> {
    return this.store.select(selectActiveWorksheet).pipe(
      filter((state) => state !== undefined && state !== null),
      take(1),
    );
  }

  public loadCatalogs(): void {
    this.store.dispatch(OrdersCatalogsActions.LoadApplicationCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadUnloadTypeCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadSpacingCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadLoadSizeCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadUnloadTimeCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadContactsAction());
    this.store.dispatch(OrdersCatalogsActions.LoadPumpCapacityCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadPointOfDeliveryAction());
    this.store.dispatch(OrdersCatalogsActions.LoadFeatureTemperatureCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadFeatureIceQuantityCatalogAction());
    this.store.dispatch(OrdersCatalogsActions.LoadFeaturePumpPrimerAction());
  }

  public resetState(): void {
    this.store.dispatch(ShipmentLocationWorkSheetActions.resetShipmentLocationWorkSheet());
    this.store.dispatch(OrdersCatalogsActions.SingleCatalogsResetAction());
    this.store.dispatch(OrdersFormsActions.resetOrderForm());
    this.store.dispatch(ScheduleDatesActions.resetScheduleDate());
    this.store.dispatch(OrdersCatalogsActions.ResetValueAdded());
    this.store.dispatch(new fromDCOActions.ResetComponentState());
    this.store.dispatch(new ResetBuilderAction());
  }

  /**
   * Returns the maximum number of allowed orders.
   * Uses a configured parameter if available; otherwise falls back to a default from the store.
   *
   * @returns {Observable<number>} Observable emitting the maximum order count.
   */
  public getMaximumOfOrders(): Observable<number> {
    return this.store.select(getMaximumNumberOfOrders).pipe(
      withLatestFrom(this.store.select(getParameters)),
      take(1),
      map(([offerSlots, parameters]) => {
        const maximumCountParam = parameters.find(
          (parameterItem) => parameterItem.parameterDesc === ORDER_LIMIT_KEY && parameterItem.businessLineId == 1,
        )?.parameterValue;

        return maximumCountParam ? Number(maximumCountParam) : offerSlots;
      }),
    );
  }

  public getOfferSlots(): Observable<number> {
    return this.store.select(getOfferSlots).pipe(
      take(1),
      map((offerSlots) => {
        return offerSlots;
      }),
    );
  }

  // selectors

  /**
   * Get all the order forms entities from the state.
   * Test these entities to find at least one created order.
   * @returns boolean
   */
  public testSomeOrdersFormsAreConfirmed(): Observable<boolean> {
    return this.store.select(getOrdersForms).pipe(
      map((entities) => {
        const entitiesAsArr = Object.values(entities);
        if (entitiesAsArr.length === 0) {
          return false;
        }
        return entitiesAsArr.some((entity) => !!entity.orderDraft && !!entity.orderConfirmation);
      }),
    );
  }

  public getOrdersForms() {
    return this.store.select(getOrdersForms).pipe(
      map((entities) => {
        const forms: OrderForm[] = [];
        for (const key in entities) {
          if (Object.prototype.hasOwnProperty.call(entities, key)) {
            forms.push(entities[key]);
          }
        }
        return forms;
      }),
    );
  }

  public validateOrdersPending(): Observable<boolean> {
    return this.store.select(getOrdersForms).pipe(
      map((formsEntites) => {
        const forms: OrderForm[] = [];
        for (const key in formsEntites) {
          if (Object.prototype.hasOwnProperty.call(formsEntites, key)) {
            forms.push(formsEntites[key]);
          }
        }
        return forms;
      }),
      map((ordersForms) => {
        return ordersForms.some((order) => order.orderConfirmation === null);
      }),
    );
  }

  /**
   * Dispatches action to request the closing of the order group.
   */
  public requestOrderGroupClose(): void {
    this.store.dispatch(OrderScheduleActions.closeGroupOrderRequest());
  }

  public getLoadingOrderConfirmation() {
    return this.store.select(loadOrderConfirmation);
  }

  public getTotalNumberOfForms() {
    return this.store.select(getTotalNumberOfOrdersForms).pipe(
      map((length) => {
        return length;
      }),
    );
  }
  public sendErrorFetchSlots() {
    this.store.dispatch(
      new fromDCOActions.FetchSlotsError({
        formID: 0,
        error: {},
      }),
    );
  }

  public getTotalNumberOfFormsWithOrderConfirmations() {
    return this.store.select(getOrderFormsWithOrderConfirmation).pipe(
      map((ordersForm) => {
        return ordersForm.length;
      }),
    );
  }

  /**
   * Creates an order draft based on the user's roles and country code.
   *
   * This method retrieves the user's roles and checks if the user has the
   * 'ShowPricesRms' role and if the country code is 'MEXICO'. If both conditions
   * are met, it calls `onlyCreateOrderDraft()` to only create the order (do not call the group and confirm api immediately)
   * to show the prices. Otherwise, it dispatches the
   * `createSimulateOrder` action, to the normal flow of the order creation (create the order, grouping and confirm).
   *
   * @returns {void}
   */
  public createOrderDraft() {
    this.getRoles$()
      .pipe(
        map((roles) => {
          const priceRol = roles.find((rol) => rol.roleCode === RolesCollection.ShowPricesRms);
          const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
          if (countryCode === COUNTRY_CODE.MEXICO && !!priceRol) {
            this.onlyCreateOrderDraft();
            return;
          }
          this.store.dispatch(OrderScheduleActions.createSimulateOrder());
        }),
      )
      .subscribe();
  }

  /**
   * Retrieves the user roles from the store as an observable.
   *
   * @returns An observable that emits the user roles.
   */
  public getRoles$() {
    return this.store.select(getUserRoles).pipe(map((roles) => roles));
  }

  /**
   * Dispatches an action to create an order draft.
   *
   * This method triggers the `onlyCreateOrder` action from the `OrderScheduleActions`
   * to initiate the creation of an order draft in the store.
   *
   * @returns {void}
   */
  public onlyCreateOrderDraft() {
    this.store.dispatch(OrderScheduleActions.onlyCreateOrder());
  }

  public getSelectedOrderForm() {
    return this.store.select(getSelectedOrderForm);
  }

  public getSelectedOrderFormWithId(formId: string) {
    return this.store.select(getFormWithId(formId));
  }

  public getAllContacts() {
    return this.store.select(getContacts);
  }

  public getAllReceivingPersons() {
    return this.store.select(getReceivingPersonEntities);
  }

  public getAvailableVapsFromContract(ContractCode: string) {
    return this.store.select(getValuesAddedForKey(ContractCode));
  }
  public getGroupRequest() {
    return this.store.select(getGroupRequest);
  }

  public getLoadingConfirmation() {
    return this.store.select(getLoadingOrderConfirmation);
  }

  // DCO selectors

  /**
   * Gets from the state the default active slot, using id and index as 0.
   * @returns Slot
   */
  public getCurrentSlot() {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    return this.store.select(getSlot).pipe(
      map((slot) => {
        if (slot && slot.timeSlots && countryCode === COUNTRY_CODE.UNITED_KINGDOM) {
          slot.timeSlots.forEach((item) => {
            item.dateDisplay = item.dateMoment.format('DD MMM');
          });
        }
        return slot;
      }),
    );
  }

  public getSlotMultipleOrderOnline() {
    return this.store.select(getSlotsMOO);
  }

  public getSpecialScenariosMooState() {
    return this.store.select(getSpecialScenariosMooState);
  }

  /**
   * Creates a single moment instance using the current selected order form
   * timestamp and the delivery time value.
   * @returns moment
   */
  public getDcoRequestDatetime() {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      map((orderForm) => {
        const requestDatetime = moment(orderForm.originalTimestamp);
        const deliveryTimeValue: any = orderForm.values[SpecFieldNames.DELIVERY_TIME];
        if (deliveryTimeValue.hours !== undefined) {
          requestDatetime.set({ hour: deliveryTimeValue.hours, minute: deliveryTimeValue.minutes });
        } else {
          const aux = new Date(deliveryTimeValue);
          requestDatetime.set({ hour: aux.getHours(), minute: aux.getMinutes() });
        }
        return requestDatetime.clone();
      }),
    );
  }
  // DCO dispatchers

  /**
   * Mark that the user wants to send the requested datetime as waiting list
   * forcing to create the order with 'In Review' as status.
   * @param checkboxValue boolean
   */
  public checkAsWaitingList(checkboxValue: boolean): void {
    this.store.dispatch(new fromDCOActions.CheckWaitingList({ formID: 0, checked: checkboxValue }));
  }

  /**
   * Prepares data for the calendar view, the workflow known as full visibility.
   */
  public setupFullVisiblitySchedule(): void {
    this.store.dispatch(new fromDCOScheduleActions.FormatDataForCalendarView({ formId: 0 }));
    this.store.dispatch(new fromDCOActions.EnableFullVisibility({ formID: 0, enable: true }));
  }

  /**
   * Stores the user selected datetime in the current slot state.
   * @param selectedMoment Moment
   */
  public storeDatetimeSlotSelection(selectedMoment: moment.Moment): void {
    this.store.dispatch(
      new fromDCOActions.SelectDatetimeSlot({
        formID: 0,
        moment: selectedMoment,
      }),
    );
  }

  public storeSlotSelection(selectedSlot: ConcreteProposalOP): void {
    this.store.dispatch(new fromDCOActions.SetSelectedSlot(selectedSlot));
  }

  /**
   * Triggers the API call to fetch for concrete proposals to show in the dco calendar.
   * @param datetimeParameter
   */
  public retrieveMoreDaysForCalendar(datetimeParameter: moment.Moment): void {
    this.store.dispatch(
      new fromDCOScheduleActions.FetchScheduleDays({
        formId: 0,
        requestDatetime: datetimeParameter,
      }),
    );
  }
  public getCustomer() {
    return this.store.select(getLegalEntity);
  }

  /**
   * Creates a moment instance with the cutoff value as a min date
   * and adds the offer days to instance a max date.
   * @returns An object with properties of type moment.
   */
  public getMinMaxMoments() {
    return this.store.select(getCutOffAndOfferDays).pipe(
      withLatestFrom(
        this.getCustomer(),
        getExperienceByName(this.store, TEMPERATURE_CONTROL.NITROGEN),
        getExperienceByName(this.store, TEMPERATURE_CONTROL.ICE),
        this.store.select(getSelectedOrderForm).pipe(take(1)),
        this.store.select(getFeatureTemperatureManagement).pipe(take(1)),
      ),
      map(([data, legalEntity, nitroExperience, iceExperience, formGroup, temperatureValues]) => {
        if (data && data.cutOffString && data.offerDays) {
          let minMoment = moment(data.cutOffString);
          const _value = _safe(
            () =>
              temperatureValues.find((value) => value.featureId === formGroup.values[SpecFieldNames.TEMPERATURE_UNDER]),
            null,
          );

          if (
            data.temperatureControlBusinessDays.length > 0 &&
            ((!!nitroExperience &&
              nitroExperience.enabled &&
              _safe(() => _value && _value.featureCode === TemperatureControlEnum.NONE, false)) ||
              (!!iceExperience &&
                iceExperience.enabled &&
                !!formGroup &&
                formGroup.values[SpecFieldNames.ICE_QUANTITY]))
          ) {
            const tmpFindLastOk = data.temperatureControlBusinessDays.find((day: BusinessDay) => day.type === 'Ok');
            if (!!tmpFindLastOk) {
              minMoment = moment(tmpFindLastOk.date);
            }
          }

          const useBusinessDaysFlag = this.featureToggle.isFeatureEnabled(Features.EnabledBusinessDays);
          const enabledBusinessDaysCutOff = this.featureToggle.isFeatureEnabled(Features.EnableBusinessDaysCutOff);
          const useBusinessDays = useBusinessDaysFlag && data.businessDays.length > 0;
          if (useBusinessDays && enabledBusinessDaysCutOff) {
            minMoment = moment(data.businessDays.find((item) => item.type === BusinessDayType.Ok).date);
          }
          const maxMoment = useBusinessDays
            ? moment(data.businessDays[data.businessDays.length - 1].date)
            : minMoment.clone().add(data.offerDays, 'days');
          return {
            cutOffDate: minMoment,
            maxOfferDate: maxMoment,
          };
        }
        return undefined;
      }),
    );
  }

  /**
   * Group the entities of the orders forms state into a single array.
   * @returns An associative array of the orders assigned by date.
   */
  public groupFormsByDate(): Observable<{ [key: string]: OrderForm[] }> {
    return this.store.select(getOrdersForms).pipe(
      map((formsEntites) => {
        const forms: OrderForm[] = [];
        for (const key in formsEntites) {
          if (Object.prototype.hasOwnProperty.call(formsEntites, key)) {
            forms.push(formsEntites[key]);
          }
        }
        return forms;
      }),
      map((ordersForms) => {
        return ordersForms.sort((a, b) => {
          if (a.requestedTimestamp < b.requestedTimestamp) {
            return -1;
          }
          if (a.requestedTimestamp > b.requestedTimestamp) {
            return 1;
          }
          return 0;
        });
      }),
      map((ordersForms) => {
        const groupedForms = ordersForms.reduce((group: { [key: string]: OrderForm[] }, item, currentIndex) => {
          if (group[item.requestedTimestamp] === undefined) {
            group[item.requestedTimestamp] = [];
          }
          item.index = currentIndex;
          group[item.requestedTimestamp].push(item);
          return group;
        }, {});
        return groupedForms;
      }),
    );
  }
  public getOrdersConfirmation(): Observable<OrderForm[]> {
    return this.store.select(getOrdersForms).pipe(
      map((formsEntites) => {
        const forms: OrderForm[] = [];
        for (const key in formsEntites) {
          if (Object.prototype.hasOwnProperty.call(formsEntites, key)) {
            forms.push(formsEntites[key]);
          }
        }
        return forms;
      }),
      map((ordersForms) => {
        return ordersForms.sort((a, b) => {
          if (a.requestedTimestamp < b.requestedTimestamp) {
            return -1;
          }
          if (a.requestedTimestamp > b.requestedTimestamp) {
            return 1;
          }
          return 0;
        });
      }),
      map((ordersForms) => {
        return ordersForms.filter((order) => order.orderConfirmation != null);
      }),
    );
  }

  /**
   * Uses a set of default values and triggers the action to create a new
   * order form entity.
   * @param timestamp Date of where the order form is going to be created
   */
  public addNewMixForm(timestamp: number) {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    const m = moment();
    let presetTime: moment.Moment;
    if (m.minute() || m.second() || m.millisecond()) {
      presetTime = m.add(1, 'hour').startOf('hour');
    } else {
      presetTime = m.startOf('hour');
    }
    const defaultValues$ = this.store.select(selectCalendarResponse).pipe(
      filter((calendarResponse) => !!calendarResponse),
      take(1),

      mergeMap((calendarResponse) => {
        return this.store.select(selectActiveShipmentLocationEntity).pipe(
          take(1),
          map((activeShipmentLocation) => {
            return {
              calendarResponse,
              shipmentLocationCode: activeShipmentLocation.shipmentLocationCode,
            };
          }),
        );
      }),
      mergeMap(({ calendarResponse, shipmentLocationCode }) => {
        return this.store.select(getOrdersCatalogsState).pipe(
          take(1),
          map((catalogs) =>
            catalogs.pointOfDeliveries.filter((pod) => pod.shipmentLocationCode === shipmentLocationCode),
          ),
          map((pointOfDeliveries) => {
            return {
              calendarResponse,
              ...(pointOfDeliveries && pointOfDeliveries.length > 0 && pointOfDeliveries[0]
                ? {
                    shipmentLocationId: pointOfDeliveries[0].shipmentLocationId,
                  }
                : { shipmentLocationId: null }),
            };
          }),
        );
      }),
      tap(({ calendarResponse, shipmentLocationId }) => {
        // Check if the feature is enabled for adding mix
        const isDefaultTimeEnabled = this.featureToggle.isFeatureEnabled(Features.DEFAULT_TIME_ADD_MOO);
        const defaultValues = {
          ...(countryCode === COUNTRY_CODE.USA
            ? {
                [SpecFieldNames.QUANTITY]: 10,
                [SpecFieldNames.LOAD_SIZE]: `${calendarResponse.loadSize}yd3`,
              }
            : countryCode === COUNTRY_CODE.MEXICO
              ? {
                  [SpecFieldNames.QUANTITY]: 1,
                } // ITS UK
              : {
                  [SpecFieldNames.QUANTITY]: 1,
                  [SpecFieldNames.UNLOAD_TYPE]: '30_MIN',
                  [APPLICATION_FIELD_NAME]: 'Base',
                  [SpecFieldNames.LOAD_SIZE]: `${calendarResponse.loadSize}yd3`,
                  ...(shipmentLocationId ? { [SpecFieldNames.POINT_OF_DELIVERY]: shipmentLocationId } : {}),
                }),
        };

        // When the feature is enabled, the default time is added to the form
        if (isDefaultTimeEnabled) {
          defaultValues[SpecFieldNames.DELIVERY_TIME] = presetTime.toLocaleString();
        }

        this.store.dispatch(OrdersFormsAction.closeUntouchedForms());

        this.store.dispatch(
          OrdersFormsAction.addForm({
            timestamp: timestamp,
            defaultValues: defaultValues,
          }),
        );
      }),
    );

    defaultValues$.subscribe();
  }

  /**
   * Dispatches an action to eliminate an order form from the entities state.
   * @param formId string
   */
  public deleteMixForm(formId: string): void {
    this.store.dispatch(OrdersFormsAction.deleteForm({ formId }));
  }

  /**
   * Sets an entity as a selected order form in the state.
   * @param id The string id of an order form (timestamp plus order counter).
   */
  public selectForm(id: string) {
    this.store.dispatch(OrdersFormsAction.selectForm({ formId: id }));
  }

  /**
   * Unselects previously selected entity.
   */
  public unselectForm() {
    this.store.dispatch(OrdersFormsAction.unselectForm());
  }

  public getFormById(id: string) {
    return this.store.select(getFormWithId(id)); //
  }

  public getCatalogProducts() {
    return this.store.select(getProducts).pipe(filter((products) => !!products));
  }

  public getCatalogProductByProductCode(productCode: string) {
    return this.store.select(getProducts).pipe(
      filter((products) => !!products),
      map((products) => products.find((product) => makeReference(product) === productCode)),
    );
  }

  public getCatalogProductByMixReference(productMixRef: string) {
    return this.store.select(getProducts).pipe(
      filter((products) => !!products),
      map((products) => products.find((product) => makeReference(product) === productMixRef)),
    );
  }

  /**
   * Dispatches an action that uses the selected order form as a source to duplicate its values over an
   * array with dates selected by the user.
   * @remarks
   * An order form must be first selected in the state.
   *
   * If the user selected dates in {@param timestamps} that go beyond the current max date in the state,
   * these new dates are added by dispatching a ScheduleDatesActions.addDate action.
   *
   * If the current country is UK, the DELIVERY_TIME field is deleted before dispatching the duplicate action.
   *
   * @param timestamps - The dates where the user wants to duplicate the selected form into.
   */
  public duplicateForMultipleDates(timestamps: number[]) {
    let maxDateSelected = 0;
    for (let i = 0; i < timestamps.length; i++) {
      if (timestamps[i] > maxDateSelected) {
        maxDateSelected = timestamps[i];
      }
    }

    const form$ = this.store.select(getSelectedOrderForm).pipe(
      withLatestFrom(this.store.select(getFeatureTemperatureManagement)),
      take(1),
      map(([orderForm, features]) => {
        const selectedOrderForm: OrderForm = JSON.parse(JSON.stringify(orderForm));
        selectedOrderForm.isBIGO = null;

        // Check if the feature is enabled for duplicating form
        const isDefaultTimeEnabled = this.featureToggle.isFeatureEnabled(Features.DEFAULT_TIME_DUPLICATE_MOO);
        if (
          selectedOrderForm.orderConfirmation &&
          selectedOrderForm.values.hasOwnProperty(SpecFieldNames.DELIVERY_TIME)
        ) {
          selectedOrderForm.values[SpecFieldNames.DELIVERY_TIME] = selectedOrderForm.orderConfirmation.scheduleOrder;
        }
        // When the feature is disabled, the default time is removed from the form
        if (isDefaultTimeEnabled === false) {
          if (selectedOrderForm.values.hasOwnProperty(SpecFieldNames.DELIVERY_TIME)) {
            delete selectedOrderForm.values[SpecFieldNames.DELIVERY_TIME];
            selectedOrderForm.valid = false;
            selectedOrderForm.invalid = true;
          }
        }
        this.store.dispatch(
          OrdersFormsAction.duplicateSelectedForm({
            dates: timestamps,
            selectedForm: selectedOrderForm,
            expandCard: this.getExpandCardTemperatureUnder(selectedOrderForm, features),
          }),
        );
      }),
    );
    form$.subscribe();
  }

  public getExpandCardTemperatureUnder(selectedOrderForm, features) {
    const temperatureUnderId = selectedOrderForm.values.temperatureUnder;
    const feature = !!features && features.find((featureAux) => featureAux.featureId === temperatureUnderId);
    return !!feature && feature.featureCode === TemperatureControlEnum.NONE;
  }

  /**
   * Retrieves the calendar callback response from the state.
   * @returns Calendar object.
   */
  public getCalendarResponse$(): Observable<Calendar> {
    return this.store.select(selectCalendarResponse).pipe(take(1));
  }

  public getCutOffAndCalendarResponse() {
    return this.store.select(getCutOffAndCalendarResponse);
  }

  public selectActiveShipmentLocation$() {
    return this.store.select(selectActiveShipmentLocationEntity);
  }
  public getSlumpProductCode(productCode: string) {
    return this.store.select(getSlumps(productCode));
  }

  public getLoadSizeCatalogs() {
    return this.store.select(getLoadSizes);
  }

  public getApplications() {
    return this.store.select(getApplications);
  }

  public getSpacings() {
    return this.store.select(getSpacings);
  }

  public getUnloadTypes() {
    return this.store.select(getUnloadTypes);
  }

  public getUnloadTimes() {
    return this.store.select(getUnloadTimes);
  }

  public getDcoEntitiesArray() {
    return this.store.select(getDcoEntitiesAsArray);
  }

  public selectSelectedShipmentLocations$() {
    return this.store.select(selectActiveShipmentLocationEntity);
  }

  public selectJobsitesStatus() {
    return this.store.select(selectJobsitesStatus);
  }

  public selectLegalEntity() {
    return this.store.select(getLegalEntity);
  }

  public selectAvailableProductTypes() {
    return this.store.select(selectAllProductTypes);
  }

  public selectAllShipmentLocations() {
    return this.store.select(selectAllShipmentLocations);
  }

  public selectShipmentLocationWorksheet(): Observable<AsyncState<ShipmentLocationWorkSheet>> {
    return this.store.select(selectWorksheetByShipmentLocation);
  }

  public selectProductTypeState() {
    return this.store.select(selectProductTypeState);
  }

  public selectActiveProductType() {
    return this.store.select(selectActiveProductTypeId);
  }

  public getExtraProducts() {
    return this.store.select(getExtraProducts);
  }

  public getUnitsForProduct() {
    return this.store.select(getUnitsByProductMap);
  }

  public updateActiveShipmentLocation(shipmentLocationId: string | number) {
    this.store.dispatch(ShipmentLocationActions.setActive({ shipmentLocationId }));
  }

  public setActiveProductLine(productTypeId: number) {
    this.store.dispatch(ProductTypeActions.setActive({ productTypeId }));
  }

  public hasUserAccessToMultipleOrderOnlineExperience$(): Observable<boolean> {
    return this.isFeatureMultipleOrderOnlineEnabled$().pipe(
      switchMap((isFeatureEnabled) => {
        if (isFeatureEnabled) {
          return this.hasUserMultipleOrderOnlineEnabled$();
        }

        return of(isFeatureEnabled);
      }),
      switchMap((isUserEnabled) => {
        if (isUserEnabled) {
          return this.hasJobsiteMultipleOrderOnlineEnabled$();
        }

        return of(isUserEnabled);
      }),
    );
  }

  private isFeatureMultipleOrderOnlineEnabled$(): Observable<boolean> {
    // @TODO
    // Is feature Multiple Order Online Experience enabled
    // this.featureToggle.isFeatureEnabled(Features.FEATURE_NAME)
    return of(true);
  }

  private hasUserMultipleOrderOnlineEnabled$(): Observable<boolean> {
    // @TODO
    // User must have the Single Order Online Experience
    // User must have the Multiple Order Online Experience
    // this.store.select(getUserRolesAll).pipe(map((userRoles) => userRoles[RolesCollection.ROLE_NAME]))
    return of(true);
  }

  private hasJobsiteMultipleOrderOnlineEnabled$(): Observable<boolean> {
    // @TODO
    // Jobsite must have the Single Order Online Experience
    // Jobsite must have the Multiple Order Online Experience
    //  this.store.select(getSelectedJobsite).pipe(map((jobsite) => ???))
    return of(true);
  }

  /**
   * Selects the state for the special scenarios, uses the selected order form
   * to test if a special scenario is in true.
   *
   * @remarks
   * An order form must be selected in the state in order for this function to complete
   * its pipe for testing the special scenario entities.
   *
   * @privateRemarks
   * US validates volume allocation, besides the special scenarios' state.
   *
   * UK only validates the special scenarios' state.
   *
   * @returns A boolean value indicating if even a single special scenario is active.
   */
  public isSpecialScenarioActive(): Observable<boolean> {
    const specialScenarios$ = this.store.select(getSpecialScenariosState).pipe(
      withLatestFrom(this.getSelectedOrderForm()),

      map(([specialScenarios, form]) => {
        const nonSpecialScenarios = [
          SPECIAL_SCENARIO_KEYS.COUNTER_OFFER,
          SPECIAL_SCENARIO_KEYS.REQUEST_TIME_AVAILABILITY,
          SPECIAL_SCENARIO_KEYS.TEMPERATURE_CONTROL,
          SPECIAL_SCENARIO_KEYS.WAITING_LIST,
        ];
        const filteredValues = Object.keys(specialScenarios.entities[form.id].values)
          .filter((key: SPECIAL_SCENARIO_KEYS) => !nonSpecialScenarios.includes(key))
          .reduce((acc, key) => {
            acc[key] = specialScenarios.entities[form.id].values[key];
            return acc;
          }, {});
        return Object.values(filteredValues);
      }),
      map((specialScenarios) => {
        return specialScenarios.some((scenario) => scenario === true);
      }),
    );
    const volumeAllocationValid$ = this.store.select(getVolumeAllocation).pipe(map((volume) => volume.invalid));
    return combineLatest([specialScenarios$, volumeAllocationValid$]).pipe(
      withLatestFrom(this.store.select(getCountry)),
      map(([[specialScenarios, volumeAllocation], country]) => {
        if (country === COUNTRY_CODE.UNITED_KINGDOM) {
          return specialScenarios;
        }
        return specialScenarios || volumeAllocation;
      }),
    );
  }

  /**
   * Verifies if each order form has a special scenario active.
   * The return of the keys are set according to a priority sort.
   * @returns A string array observable with the active keys by order form.
   * @author Fabritzio Villegas.
   */
  public getSpecialScenariosWithPriority(): Observable<string[]> {
    if (!this.activeSpecialScenariosSubscription) {
      this.activeSpecialScenariosSubscription = this.store.select(getSpecialScenariosState).subscribe((state) => {
        const scenarios = [];
        state.ids.forEach((id: string | number) => {
          const aux = state.entities[id];
          if (aux.valuesAsArray && aux.valuesAsArray.length > 0) {
            if (aux.values[SPECIAL_SCENARIO_KEYS.BIG_ORDER]) {
              scenarios[aux.formId] = SPECIAL_SCENARIO_KEYS.BIG_ORDER;
            } else if (aux.values[SPECIAL_SCENARIO_KEYS.FOREIGN_JOBSITES]) {
              scenarios[aux.formId] = SPECIAL_SCENARIO_KEYS.FOREIGN_JOBSITES;
            } else if (aux.values[SPECIAL_SCENARIO_KEYS.CERTIFIED_TRUCKS_DRIVERS]) {
              scenarios[aux.formId] = SPECIAL_SCENARIO_KEYS.CERTIFIED_TRUCKS_DRIVERS;
            } else if (aux.values[SPECIAL_SCENARIO_KEYS.NO_AVAILABILITY]) {
              scenarios[aux.formId] = SPECIAL_SCENARIO_KEYS.NO_AVAILABILITY;
            }
          }
        });
        this.activeSpecialScenarios$.next(scenarios);
      });
    }
    return this.activeSpecialScenarios$.asObservable();
  }

  public isPurchaseOrderRequired(): Observable<boolean> {
    return this.store.select(selectPurchaseOrderValidation);
  }

  public getSelectedSlot(): Observable<ConcreteProposalOP> {
    return this.store.select(getSelectedSlot);
  }

  public getSlotAndTravelTime() {
    return this.store.select(getSelectedSlotAndTravelTime);
  }

  /**
   * Uses the form value of the POD to find the whole data object of the selected
   * point of delivery.
   * @returns Point of delivery object.
   */
  public getSelectedPointOfDelivery(): Observable<any> {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      withLatestFrom(this.store.select(getPODs)),
      map(([orderForm, pointsOfDelivery]) => {
        const formControlValue = orderForm.values[SpecFieldNames.POINT_OF_DELIVERY];
        const pod = pointsOfDelivery.find((item) => item.shipmentLocationId === formControlValue);
        return {
          pointOfDeliveryId: _safe(() => pod.shipmentLocationId),
          pointOfDeliveryCode: _safe(() => pod.shipmentLocationCode),
          pointOfDeliveryDesc: _safe(() => pod.shipmentLocationDesc),
          pointOfDeliveryType: {
            pointOfDeliveryTypeId: _safe(() => pod.shipmentLocationType.shipmentLocationTypeId),
            pointOfDeliveryTypeCode: _safe(() => pod.shipmentLocationType.shipmentLocationTypeCode),
          },
        };
      }),
    );
  }

  public getBigOrderVolume() {
    return this.store.select(bigOrderQty);
  }

  public isBigOrderActive(formId): Observable<boolean> {
    return this.store.select(getSpecialScenariosWithFormId(formId)).pipe(
      take(1),
      map((specialScenarios) => {
        if (specialScenarios) {
          return _safe(() => specialScenarios.values[SPECIAL_SCENARIO_KEYS.BIG_ORDER], false);
        }
        return false;
      }),
    );
  }

  public isBigOrderByCalendar(formValues: KeyValues): Observable<boolean> {
    return this.store.select(getCalendarState).pipe(
      take(1),
      map((calendar) => {
        const quantity = formValues[SpecFieldNames.QUANTITY] as number;
        if (quantity > calendar?.bigOrder?.quantity) {
          return true;
        }
        return false;
      }),
    );
  }

  /**
   * @param formId
   * This method receives the formId and returns an observable with a boolean
   * value that indicates if the special scenario is active in the form.
   * @returns Observable<boolean>
   */
  public isOutOfServiceActive(formId): Observable<boolean> {
    return this.store.select(getSpecialScenariosWithFormId(formId)).pipe(
      take(1),
      map((specialScenarios) => {
        if (specialScenarios) {
          return _safe(() => specialScenarios.values[SPECIAL_SCENARIO_KEYS.OUT_SERVICE_HOURS], false);
        }
        return false;
      }),
    );
  }

  isForeignJobsite(): Observable<boolean> {
    return this.store.select(getErrorSlotCode).pipe(
      take(1),
      map((errorCode) => {
        if (Number(errorCode) === ErrorCode.FOREIGN_JOBSITES) {
          return true;
        } else {
          return false;
        }
      }),
    );
  }

  isUsingNitroExperience(): Observable<boolean> {
    return getExperienceByName(this.store, TEMPERATURE_CONTROL.NITROGEN).pipe(
      take(1),
      map((nitroExperience) => !!nitroExperience && nitroExperience.enabled),
    );
  }

  isUsingIceExperience(): Observable<boolean> {
    return getExperienceByName(this.store, TEMPERATURE_CONTROL.ICE).pipe(
      take(1),
      map((iceExperience) => !!iceExperience && iceExperience.enabled),
    );
  }

  public getTemperatureUnderById(id: number) {
    return this.store.select(getFeatureTemperatureManagement).pipe(
      filter((state) => !!state),
      map((features: ImFeature[]) => features.find((feature) => feature.featureId === id)),
    );
  }

  public getIceQuantityById(featureId: number) {
    return this.store.select(getFeatureIceQuantity).pipe(
      filter((state) => !!state),
      map((features: ImFeature[]) => features.find((feature) => feature.featureId === featureId)),
    );
  }

  public getAvailableValuesAdded(contractFormValue: string, idFormValue: string) {
    return this.store.select(getAvailableValuesAdded).pipe(
      filter((valuesAdded) => !!valuesAdded),
      filter((valuesAdded) => {
        const keyName = `${contractFormValue}_${idFormValue}`;
        return !!valuesAdded[keyName];
      }),
      map((valuesAdded) => {
        const keyName = `${contractFormValue}_${idFormValue}`;
        if (!!valuesAdded && !!valuesAdded[keyName] && !!valuesAdded[keyName] && valuesAdded[keyName].length > 0) {
          return valuesAdded[keyName];
        }
        return [];
      }),
    );
  }

  public getCalendarStateTemperatureControlFlag() {
    return this.store.select(getCalendarState).pipe(
      filter((state) => state !== null && state !== undefined),
      take(1),
      map((calendarState) => {
        return calendarState.tempControlFlag;
      }),
    );
  }

  public getExperiences() {
    return this.store.select(selectAllExperiences).pipe(
      filter((state) => state !== null && state !== undefined),
      take(1),
      map((experiences) => {
        return experiences[0].experienceCode;
      }),
    );
  }

  /**
   * To reset pump controls to default values
   * @param formGroup form that contains the pump controls
   * @returns Observable<void>
   */
  public resetStatePumpControls(formGroup: UntypedFormGroup) {
    const id = formGroup.get(SpecFieldNames.ID).value;
    return this.store.select(getPumpPrimerDefaultQuantity).pipe(
      take(1),
      tap((defaultQuantityFeature: ImFeature) => {
        if (id !== undefined) {
          const quantity = !!defaultQuantityFeature ? +defaultQuantityFeature.featureValue : 3;
          const resetValue = undefined;
          formGroup.controls.primerQuantity.setValue(quantity);
          formGroup.controls.pumpPrimerProduct.setValue(resetValue);
          formGroup.controls.primerSlump.setValue(resetValue);
          formGroup.controls.primerArrivalBeforeDelivery.setValue(resetValue);

          this.store.dispatch(
            OrdersFormsActions.updateForm({
              formId: id,
              formControlName: SpecFieldNames.PRIMER_QUANTITY,
              formControlvalue: quantity,
              formIsValid: formGroup.valid,
            }),
          );

          this.store.dispatch(
            OrdersFormsActions.updateForm({
              formId: id,
              formControlName: SpecFieldNames.PUMP_PRIMER_MIX,
              formControlvalue: resetValue,
              formIsValid: formGroup.valid,
            }),
          );

          this.store.dispatch(
            OrdersFormsActions.updateForm({
              formId: id,
              formControlName: SpecFieldNames.PRIMER_SLUMP,
              formControlvalue: resetValue,
              formIsValid: formGroup.valid,
            }),
          );

          this.store.dispatch(
            OrdersFormsActions.updateForm({
              formId: id,
              formControlName: SpecFieldNames.PRIMER_ARRIVAL_BEFORE_DELIVERY,
              formControlvalue: resetValue,
              formIsValid: formGroup.valid,
            }),
          );
        }
      }),
      ignoreElements(),
    );
  }

  /**
   * Reset unload type value with pump primer rules
   */
  public resetUnloadTypeValue() {
    const toggle = this.featureToggle.isFeatureEnabled(Features.PUMP_PRIMER);
    return this.store.select(getUnloadTypes).pipe(
      map((unloadTypes) => {
        const getUnloadTypeDefault =
          !!unloadTypes && unloadTypes.length > 0
            ? unloadTypes.find(
                (uType) =>
                  uType.unloadTypeCode === UNLOAD_TYPE_DEFAULT1 || uType.unloadTypeCode === UNLOAD_TYPE_DEFAULT2,
              )
            : null;
        return toggle ? undefined : getUnloadTypeDefault.unloadTypeCode;
      }),
    );
  }

  /**
   * to check if unload type is a valid pump and check feature toggle is ON
   * @param value of unload type
   * @returns true if value is a valid pump unload type
   */
  public isValidPumpUnloadType(value: string) {
    const toggle = this.featureToggle.isFeatureEnabled(Features.PUMP_PRIMER);
    return this.store.select(getPumpMethodValue).pipe(
      take(1),
      map((features: { [key: string]: ImFeature }) => {
        const defaultPumpPrimerValue = _safe(() => features[PP_PUMP_MED_VALUE].featureValue, '');
        const unloadTypePumpPrimer = _safe(() => features[PP_PUMP_MED_MULTIPLE_VALUE].featureValue, '');
        const source = !!features[PP_PUMP_MED_MULTIPLE_VALUE]
          ? unloadTypePumpPrimer.split(',')
          : [defaultPumpPrimerValue];
        return toggle && isPumpUnloadType(value, source);
      }),
    );
  }

  /**
   * Check if selected form has an "Unload Type" of class "Pump" selected
   * @returns true if an "Unload Type" of class "Pump" is selected
   */
  public isUnloadTypePumpSelected() {
    return this.getSelectedOrderForm().pipe(
      take(1),
      switchMap((orderForm) => {
        const unloadType = <string>orderForm.values[SpecFieldNames.UNLOAD_TYPE];
        return this.isValidPumpUnloadType(unloadType).pipe(
          map((isPumpUnloadTypeValue) => {
            return isPumpUnloadTypeValue;
          }),
        );
      }),
    );
  }

  /**
   * Check if selected form has pump primer selected
   *
   * @returns true if pump primer is selected
   */
  public isPumpPrimerSelected() {
    return this.getSelectedOrderForm().pipe(
      take(1),
      switchMap((orderForm) => {
        const unloadType = <string>orderForm.values[SpecFieldNames.UNLOAD_TYPE];
        const pumpPrimerMix = <string>orderForm.values[SpecFieldNames.PUMP_PRIMER_MIX];
        return this.isValidPumpUnloadType(unloadType).pipe(
          map((isPumpUnloadTypeValue) => {
            return isPumpUnloadTypeValue && !!pumpPrimerMix && pumpPrimerMix !== PUMP_NONE_CODE;
          }),
        );
      }),
    );
  }
}
